import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Bell, CloudOff, Home, LogOut, MessagesSquare, RefreshCw, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { clearLast, flush, useQueue } from "./offline";

const OfflineBar = () => {
  const q = useQueue();
  if (q.online && !q.pending && !q.last) return null;
  return (
    <div className="bg-[#fff6e0] text-[#5c3d00] text-sm px-4 py-2 space-y-1" data-testid="terreno-offline-bar">
      {(!q.online || q.pending > 0) && <div className="flex items-center gap-2">
        <CloudOff size={16} /><b data-testid="terreno-offline-status">{q.online ? "Enviando" : "Sin señal"} · {q.pending} pendiente(s)</b>
        {q.online && q.pending > 0 && <button data-testid="terreno-sync-now" onClick={flush} className="ml-auto flex items-center gap-1 font-semibold"><RefreshCw size={14} />Enviar ahora</button>}
      </div>}
      {q.last && <div className="flex items-start gap-2" data-testid="terreno-sync-result">
        <div className="flex-1">Último envío: <b>{q.last.ok} enviado(s)</b>{q.last.stopped && <div className="text-xs font-semibold" data-testid="terreno-sync-stopped">{q.last.stopped}</div>}{q.last.errors.length > 0 && <>, <b className="text-[#b42318]">{q.last.errors.length} rechazado(s)</b>
          <ul className="text-xs text-[#b42318] list-disc pl-4">{q.last.errors.map((er, i) => <li key={i}>{er}</li>)}</ul></>}</div>
        <button data-testid="terreno-sync-dismiss" onClick={clearLast}><X size={16} /></button>
      </div>}
    </div>
  );
};

const useCounts = (pathname) => {
  const [c, setC] = useState({ chat: 0, notif: 0 });
  useEffect(() => {
    const load = () => Promise.all([api.get("/chat/unread"), api.get("/notifications", { params: { unread: true, limit: 1 } })])
      .then(([a, b]) => setC({ chat: a.data.unread, notif: b.data.unread })).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [pathname]);
  return c;
};

const Tab = ({ to, icon: Icon, label, count, testid, end }) => (
  <NavLink to={to} end={end} data-testid={testid}
    className={({ isActive }) => `relative flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-semibold ${isActive ? "text-[#5CD500]" : "text-white/70"}`}>
    <Icon size={24} />{label}
    {count > 0 && <span className="absolute top-1 left-1/2 ml-2 rounded-full bg-[#5CD500] px-1.5 text-[10px] font-bold text-[#0f1a08]">{count}</span>}
  </NavLink>
);

export default function TerrenoLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const counts = useCounts(location.pathname);
  if (!user) return <Navigate to="/terreno/ingresar" replace state={{ from: location.pathname }} />;
  if (user.must_change_password) return <Navigate to="/mi-cuenta" replace />;
  const out = async () => { await logout(); navigate("/terreno/ingresar", { state: { from: "/terreno" } }); };
  return (
    <div className="min-h-[100dvh] bg-[#f3f5f1] flex flex-col" data-testid="terreno-app">
      <header className="sticky top-0 z-20 bg-[#17210f] text-white px-4 pt-[max(env(safe-area-inset-top),12px)] pb-3 flex items-center gap-3">
        <div className="syc-logo !bg-[#258F00] !text-white">S&amp;C</div>
        <div className="flex-1 min-w-0"><div className="font-extrabold tracking-[0.08em] text-sm">SYC TERRENO</div><div className="text-[11px] text-white/60 truncate" data-testid="terreno-user">{user.name}</div></div>
      </header>
      <OfflineBar />
      <main className="flex-1 px-4 py-4 pb-28 max-w-xl w-full mx-auto"><Outlet /></main>
      <nav className="fixed bottom-0 inset-x-0 z-20 bg-[#17210f] flex pb-[env(safe-area-inset-bottom)]" data-testid="terreno-tabbar">
        <Tab to="/terreno" end icon={Home} label="Inicio" testid="terreno-tab-home" />
        <Tab to="/terreno/mensajes" icon={MessagesSquare} label="Mensajes" count={counts.chat} testid="terreno-tab-messages" />
        <Tab to="/terreno/avisos" icon={Bell} label="Avisos" count={counts.notif} testid="terreno-tab-notifications" />
        <button onClick={out} data-testid="terreno-tab-logout" className="flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-semibold text-white/70"><LogOut size={24} />Salir</button>
      </nav>
    </div>
  );
}
